import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Flag } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const REASONS = [
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'copyright', label: 'Copyright infringement' },
  { value: 'misleading', label: 'Misleading or false information' },
  { value: 'other', label: 'Other' },
];

/**
 * Inline report dialog (fixed overlay).
 * Props:
 *   open      {boolean}
 *   onClose   {() => void}
 *   workId    {string}  the work UUID
 */
export default function ReportDialog({ open, onClose, workId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      navigate('/auth');
      return;
    }

    if (!reason) {
      toast({ title: 'Select a reason', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      // Prevent duplicate open reports by the same user for the same work
      const { data: existing } = await supabase
        .from('content_reports')
        .select('id')
        .eq('reporter_user_id', user.id)
        .eq('work_id', workId)
        .eq('status', 'open')
        .maybeSingle();

      if (existing) {
        toast({
          title: 'Already reported',
          description: 'You have an open report for this title. We are reviewing it.',
        });
        onClose();
        return;
      }

      const { error } = await supabase.from('content_reports').insert({
        work_id: workId,
        reporter_user_id: user.id,
        reason,
        details: details.trim() || null,
      });

      if (error) throw error;

      toast({ title: 'Report submitted', description: 'Thank you — our team will review it.' });
      setReason('');
      setDetails('');
      onClose();
    } catch (err) {
      console.error('Report error:', err);
      toast({ title: 'Failed to submit report', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-dialog-title"
      >
        <div className="flex items-center gap-2 mb-5">
          <Flag className="h-4 w-4 text-destructive" />
          <h2 id="report-dialog-title" className="text-base font-semibold">
            Report content
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Reason</label>
            <div className="grid grid-cols-1 gap-2">
              {REASONS.map((r) => (
                <label
                  key={r.value}
                  className={cn(
                    'flex items-center gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer text-sm transition-colors',
                    reason === r.value
                      ? 'border-destructive/50 bg-destructive/10 text-foreground'
                      : 'border-border hover:border-border/80 hover:bg-secondary/40 text-muted-foreground'
                  )}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={() => setReason(r.value)}
                    className="accent-destructive"
                  />
                  {r.label}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="report-details" className="text-sm font-medium text-foreground">
              Details <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <textarea
              id="report-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Describe the issue in more detail..."
              rows={3}
              maxLength={1000}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              size="sm"
              disabled={!reason || submitting}
            >
              {submitting ? 'Submitting...' : 'Submit report'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
